import { Link, useLocation } from "react-router-dom";
import { ChevronDown, User, LogOut, GraduationCap } from "lucide-react";
import { DashboardSwitcher } from "@/components/DashboardSwitcher";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const link = (active) => `px-2.5 xl:px-3 py-2 rounded-lg text-sm font-medium transition-colors ${active ? "text-blue-600 bg-blue-50" : "text-slate-600 hover:text-blue-600"}`;

const Explore = ({ links }) => (
  <DropdownMenu>
    <DropdownMenuTrigger className={`${link(false)} flex items-center gap-1 outline-none`} data-testid="nav-explore">Explore<ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-52">
      {links.map(l => <DropdownMenuItem key={l.to} asChild><Link to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</Link></DropdownMenuItem>)}
    </DropdownMenuContent>
  </DropdownMenu>
);

const Account = ({ user, logout }) => (
  <DropdownMenu>
    <DropdownMenuTrigger className="flex items-center gap-1.5 h-9 pl-1 pr-2 rounded-full border border-slate-200 hover:border-slate-400 outline-none transition-colors" data-testid="nav-account">
      <span className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{(user.name || user.email || "?")[0].toUpperCase()}</span>
      <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <div className="px-2 py-1.5"><p className="text-sm font-semibold truncate">{user.name}</p><p className="text-xs text-slate-500 truncate">{user.email}</p></div>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild><Link to="/profile" data-testid="nav-profile"><User className="w-4 h-4 mr-2" />Profile</Link></DropdownMenuItem>
      <DropdownMenuItem onClick={logout} data-testid="nav-logout-btn"><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const DesktopNav = ({ user, logout, links }) => {
  const { pathname } = useLocation();
  const staff = user && ["admin", "sales_rep"].includes(user.role);
  const tools = !user ? [] : [
    user.role === "admin" && ["/admin", "Admin", "nav-admin"],
    staff && ["/webadvisor", "Web Advisor", "nav-webadvisor"],
    staff && ["/call-center", "Call Center", "nav-call-center"],
    staff && ["/sales/dashboard", "Sales", "nav-sales-dash"],
    !["member", "applicant", "demo"].includes(user.role) && ["/conference", "Meetings", "nav-conference"],
    staff && ["/academy", "Academy", "nav-academy"],
  ].filter(Boolean);
  return (
    <>
      <div className="hidden lg:flex items-center gap-0.5 xl:gap-1 whitespace-nowrap min-w-0" data-testid="nav-desktop-links">
        {user ? (user.role !== "demo" && <Explore links={links} />) : links.map(l => <Link key={l.to} to={l.to} className={link(pathname === l.to)} data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</Link>)}
        {tools.map(([to, label, id]) => <Link key={to} to={to} className={`${link(pathname.startsWith(to))} flex items-center gap-1`} data-testid={id}>{to === "/academy" && <GraduationCap className="w-4 h-4" />}{label}</Link>)}
      </div>
      <div className="hidden lg:flex items-center gap-2 whitespace-nowrap shrink-0">
        {user ? <>
          {user.role === "admin" ? <DashboardSwitcher /> : user.role === "member" ? <Link to="/client/dashboard" className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm" data-testid="nav-client-portal">My business</Link>
            : <Link to={({ sales_rep: "/sales", referral_agent: "/agent", applicant: "/careers/portal", demo: "/demo/app" }[user.role] || "/dashboard")} className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm" data-testid="nav-dashboard-btn">{user.role === "applicant" ? "My application" : "Dashboard"}</Link>}
          <Account user={user} logout={logout} />
        </> : <>
          <Link to="/login" className="border border-slate-300 hover:border-slate-400 text-slate-700 font-medium px-4 py-2 rounded-lg transition-colors text-sm" data-testid="nav-login-btn">Login</Link>
          <Link to="/register" className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 py-2 rounded-lg transition-colors text-sm" data-testid="nav-register-btn">Get Started</Link>
        </>}
      </div>
    </>
  );
};
